import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { jwtDecode } from 'jwt-decode';
import logger from '../../utils/logger';
import { authStorage } from '../../utils/storage';
import LoginEmail from './LoginEmail';
import Wordmark from './Wordmark';
import './../styles/general/Login.css';

const LoginWrapper = () => {
  const [authMode, setAuthMode]       = useState(null);
  const [isLoading, setIsLoading]     = useState(true);
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  // Form state
  const [username, setUsername]       = useState('');
  const [password, setPassword]       = useState('');
  const [error, setError]             = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { setAuth } = useAuth();

  useEffect(() => {
    const checkEnvironment = async () => {
      try {
        const response = await fetch('/api/auth/environment');
        const data = await response.json();
        setAuthMode(data.authMode);
      } catch {
        setAuthMode('passwordless');
      } finally {
        setIsLoading(false);
      }
    };
    checkEnvironment();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        authStorage.setAuthData(data);
        try {
          const decoded = jwtDecode(data.token);
          logger.log('LoginWrapper', 'Login successful', { username: decoded.username });
          setAuth({ token: data.token, isAuthenticated: true, user: decoded });
        } catch (err) {
          logger.error('LoginWrapper', 'Error decoding token', err);
        }
        navigate('/metrics', { replace: true });
      } else {
        setError(data.message || 'Login failed. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };


  // ── Loading ──────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="login-page">
        <div className="login-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          <span className="login-spinner" />
        </div>
      </div>
    );
  }

  // ── Passwordless / email mode ────────────────────────────────────────────
  if (authMode !== 'traditional' || showEmailLogin) {
    return (
      <>
        <LoginEmail />
        {authMode === 'traditional' && (
          <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
            <button
              onClick={() => setShowEmailLogin(false)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-sans)', letterSpacing: '0.03em' }}
            >
              ← Back to devuser login
            </button>
          </div>
        )}
      </>
    );
  }

  // ── Traditional login (staging / dev) ────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-card">

        {/* Brand */}
        <div className="login-brand">
          <Wordmark size={36} />
          <div className="login-brand-name">DATAHOUSE</div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <p className="login-heading">Sign in to your account</p>

          <div className="login-field">
            <label className="login-label" htmlFor="username">Username</label>
            <input
              id="username"
              className="login-input"
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="login-field">
            <label className="login-label" htmlFor="password">Password</label>
            <input
              id="password"
              className="login-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={isSubmitting}
              required
            />
          </div>

          <button type="submit" className="login-submit" disabled={isSubmitting}>
            {isSubmitting && <span className="login-spinner" />}
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>

          {error && <div className="login-error">{error}</div>}
        </form>

        {/* Footer */}
        <div className="login-footer">
          <span className="login-footer-dot" />
          <span className="login-footer-text">
            Datahouse · Dailey OS · Staging
          </span>
        </div>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button
            onClick={() => setShowEmailLogin(true)}
            style={{ background: 'none', border: 'none', color: 'var(--fg-3)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-sans)', letterSpacing: '0.03em', textDecoration: 'underline' }}
          >
            Test email login
          </button>
        </div>

      </div>
    </div>
  );
};

export default LoginWrapper;
